"use client";

import type { RefObject } from "react";

import { LeadFormTakeoverDialog } from "./LeadFormTakeoverDialog";

type LeadFormStepSurfaceProps = {
  /** The active step's analytics name (step.metricsLabel ?? step.id), forwarded to the takeover Dialog's exit tracking. */
  analyticsStepName: string;
  children: React.ReactNode;
  /** The lead `<form>` element, passed to the takeover Dialog as its portal container. */
  formElement: HTMLFormElement | null;
  /** Focus target when the takeover closes — see use-lead-form-takeover.ts. */
  inlineStepRegionRef: RefObject<HTMLDivElement | null>;
  isTakeoverOpen: boolean;
  onCloseTakeover: () => void;
  phoneNumber?: string;
  /** The active step's name, used as the takeover Dialog's accessible name. */
  title: string;
};

/**
 * Renders the step region either inline or inside the fullscreen takeover
 * Dialog, based on state from use-lead-form-takeover.ts.
 */
function LeadFormStepSurface({
  analyticsStepName,
  children,
  formElement,
  inlineStepRegionRef,
  isTakeoverOpen,
  onCloseTakeover,
  phoneNumber,
  title,
}: LeadFormStepSurfaceProps) {
  if (isTakeoverOpen) {
    // formElement is only null before mount, before isTakeoverOpen can be
    // true — otherwise container would be null and Radix would silently
    // fall back to a body portal, detaching the submit button.
    return (
      <LeadFormTakeoverDialog
        analyticsStepName={analyticsStepName}
        container={formElement}
        onClose={onCloseTakeover}
        phoneNumber={phoneNumber}
        title={title}
      >
        {children}
      </LeadFormTakeoverDialog>
    );
  }

  return (
    // Focus target for use-lead-form-takeover.ts's close focus-restore
    // effect. Plain block div, not flex-col — stepRegion's fragment
    // siblings need block flow (same constraint as LeadFormTakeoverDialog's
    // children wrapper).
    <div className="outline-none" ref={inlineStepRegionRef} tabIndex={-1}>
      {children}
    </div>
  );
}

export { LeadFormStepSurface };
